/**
 * EAF Theme Extension
 * Shared MUI palette extension for all EAF modules (Roof, Sidewall, etc.)
 *
 * This extends the app's base theme with EAF-specific UI colors.
 * Domain colors (panel identification) remain in module-specific constants.
 */

import {
  Viewer3DColors,
  StatusIndicatorColors,
  TreePanelColors,
} from "./colors";

/**
 * Extend MUI theme with EAF-specific palette
 *
 * Palette: Runtime type - describes the final resolved palette object
 * PaletteOptions: Input type - used when creating theme with createTheme()
 */
declare module "@mui/material/styles" {
  interface Palette {
    eqp: {
      // 3D Viewer UI colors
      viewer: {
        tooltipBackground: string;
        loadingOverlay: string;
        canvasGradient: {
          start: string;
          middle: string;
          end: string;
        };
        selectionHighlight: string;
      };
      // Status indicator colors (for legends and badges)
      status: {
        ok: string;
        empty: string;
        warning: string;
        alert: string;
      };
      // Tree panel UI colors
      tree: {
        offlineRowBackground: string;
      };
    };
  }

  interface PaletteOptions {
    eaf?: {
      viewer?: {
        tooltipBackground?: string;
        loadingOverlay?: string;
        canvasGradient?: {
          start?: string;
          middle?: string;
          end?: string;
        };
        selectionHighlight?: string;
      };
      status?: {
        ok?: string;
        empty?: string;
        warning?: string;
        alert?: string;
      };
      tree?: {
        offlineRowBackground?: string;
      };
    };
  }
}

/**
 * EAF theme extension object
 * Use this to extend the base theme with EAF-specific colors
 *
 * Usage in index.tsx:
 * ```typescript
 * const baseTheme = useTheme();
 * const extendedTheme = createTheme({
 *   ...baseTheme,
 *   palette: { ...baseTheme.palette, ...eqpThemeExtension.palette },
 * });
 * ```
 *
 * Then access colors via: theme.palette.eqp.viewer.tooltipBackground
 */
export const eqpThemeExtension = {
  palette: {
    eqp: {
      // 3D Viewer UI colors
      viewer: {
        tooltipBackground: Viewer3DColors.TOOLTIP_BACKGROUND,
        loadingOverlay: Viewer3DColors.LOADING_OVERLAY,
        canvasGradient: {
          start: Viewer3DColors.CANVAS_GRADIENT_START,
          middle: Viewer3DColors.CANVAS_GRADIENT_MIDDLE,
          end: Viewer3DColors.CANVAS_GRADIENT_END,
        },
        selectionHighlight: Viewer3DColors.SELECTION_HIGHLIGHT,
      },
      // Status indicator colors
      status: {
        ok: StatusIndicatorColors.OK,
        empty: StatusIndicatorColors.EMPTY,
        warning: StatusIndicatorColors.WARNING,
        alert: StatusIndicatorColors.ALERT,
      },
      // Tree panel UI colors
      tree: {
        offlineRowBackground: TreePanelColors.OFFLINE_ROW_BACKGROUND,
      },
    },
  },
};
